<template>
  <div>
    <v-data-table
      class="d-none d-md-block"
      :headers="headers"
      :items="contacts"
      :loading="loading"
      hide-default-footer
      :items-per-page="-1"
      :no-data-text="emptyText"
      mobile-breakpoint="0"
    >
      <template v-slot:item.name="{ item }">
        <div class="contact-identity">
          <div class="contact-identity__name" :title="item.name">
            {{ item.name }}
          </div>
          <div
            v-if="formatAddress(item)"
            class="contact-identity__address"
            :title="formatAddress(item)"
          >
            {{ formatAddress(item) }}
          </div>
        </div>
      </template>
      <template v-slot:item.tier="{ item }">
        <v-chip v-if="item.tier" x-small :color="tierColor(item.tier)" dark>
          Tier {{ item.tier }}
        </v-chip>
      </template>
      <template v-slot:item.lastTouch="{ item }">
        {{ item.lastTouch ? formatDate(item.lastTouch) : "Never" }}
      </template>
      <template v-slot:item.nextDue="{ item }">
        <span :class="item.overdue ? 'red--text font-weight-bold' : ''">
          {{ item.nextDue ? formatDate(item.nextDue) : "—" }}
        </span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="item.phone"
          icon
          small
          :href="`tel:${item.phone}`"
          title="Call"
        >
          <v-icon small>mdi-phone</v-icon>
        </v-btn>
        <v-btn
          v-if="item.phone"
          icon
          small
          :href="`sms:${item.phone}`"
          title="Text"
        >
          <v-icon small>mdi-message-text</v-icon>
        </v-btn>
        <v-btn
          small
          color="primary"
          text
          :to="{ path: '/crm/log', query: { contact: item.id } }"
        >
          Log
        </v-btn>
      </template>
    </v-data-table>

    <div class="d-md-none">
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
      <div
        v-else-if="!contacts.length"
        class="pa-4 text-center grey--text caption"
      >
        {{ emptyText }}
      </div>
      <v-list v-else class="pa-0" two-line>
        <template v-for="(item, index) in contacts">
          <v-divider v-if="index > 0" :key="`divider-${item.id}`"></v-divider>
          <v-list-item :key="item.id" class="px-4 py-2 align-start">
            <v-list-item-content class="contact-identity py-1">
              <v-list-item-title
                class="contact-identity__name"
                :title="item.name"
              >
                {{ item.name }}
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="formatAddress(item)"
                class="contact-identity__address"
                :title="formatAddress(item)"
              >
                {{ formatAddress(item) }}
              </v-list-item-subtitle>
              <div class="caption mt-1 d-flex align-center flex-wrap">
                <span
                  :class="item.overdue ? 'red--text font-weight-bold' : ''"
                >
                  {{ item.nextDue ? formatDate(item.nextDue) : "No due date" }}
                </span>
                <v-chip
                  v-if="item.tier"
                  x-small
                  :color="tierColor(item.tier)"
                  dark
                  class="ml-2"
                >
                  Tier {{ item.tier }}
                </v-chip>
              </div>
            </v-list-item-content>
            <v-list-item-action class="ma-0 ml-2 align-self-center">
              <div class="d-flex align-center flex-nowrap">
                <v-btn
                  v-if="item.phone"
                  icon
                  small
                  :href="`tel:${item.phone}`"
                  title="Call"
                >
                  <v-icon small>mdi-phone</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.phone"
                  icon
                  small
                  :href="`sms:${item.phone}`"
                  title="Text"
                >
                  <v-icon small>mdi-message-text</v-icon>
                </v-btn>
                <v-btn
                  small
                  color="primary"
                  text
                  :to="{ path: '/crm/log', query: { contact: item.id } }"
                >
                  Log
                </v-btn>
              </div>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
    </div>
  </div>
</template>

<script>
import format from "date-fns/format";

export default {
  props: {
    contacts: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: "No contacts" }
  },
  data() {
    return {
      headers: [
        { text: "Name", value: "name" },
        { text: "Tier", value: "tier", width: "90px" },
        { text: "Type", value: "contactType.name", width: "140px" },
        { text: "Last Touch", value: "lastTouch", width: "120px" },
        { text: "Next Due", value: "nextDue", width: "120px" },
        { text: "", value: "actions", sortable: false, width: "160px" }
      ]
    };
  },
  methods: {
    formatAddress(contact) {
      if (!contact) return "";
      let city = (contact.city || "").trim();
      let stateZip = [contact.state, contact.zip].filter(
        value => value !== null && value !== undefined && `${value}`.trim() !== ""
      )
        .join(" ");
      let locality = [city, stateZip].filter(Boolean).join(", ");
      return [(contact.address || "").trim(), locality]
        .filter(Boolean)
        .join(", ");
    },
    formatDate(date) {
      // Date-only strings (YYYY-MM-DD) parse as UTC midnight; force local time
      let d = /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? new Date(`${date}T00:00:00`)
        : new Date(date);
      return format(d, "MMM dd");
    },
    tierColor(tier) {
      const colors = { 1: "green", 2: "blue", 3: "grey" };
      return colors[tier] || "grey";
    }
  }
};
</script>

<style scoped lang="scss">
.contact-identity {
  min-width: 0;
  max-width: 100%;
  padding: 4px 0;
}

.contact-identity__name,
.contact-identity__address {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-identity__address {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  line-height: 1.3;
  margin-top: 2px;
}

::v-deep .v-data-table table {
  table-layout: fixed;
}

::v-deep .v-data-table td {
  vertical-align: middle;
}

::v-deep .v-list-item__content {
  min-width: 0;
}

::v-deep .v-list-item__action {
  flex: 0 0 auto;
}
</style>
